import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  CircleMarker,
  Polyline,
  useMap,
  useMapEvents,
  ZoomControl,
  AttributionControl,
} from "react-leaflet";
import { useNavigate } from "react-router-dom";
import L from "leaflet";
import { Loader2, Expand, LocateFixed, Map as MapIcon, MapPin, Satellite, Heart } from "lucide-react";
import OpenFreeMapLayer from "./OpenFreeMapLayer";
import { getCachedLocationIcon } from "../lib/locationMarkerIcons";
import { hasUsableImageUrl } from "../lib/placeholderImages";
import { normalizeLocationMapMedia } from "../lib/assetUrl";
import { formatLocationAddress, hasFormattedAddress } from "../lib/locationAddress";
import { isFoodCategoryDisplayName } from "../lib/foodCategoryDisplay";
import { locationService } from "../services/locationService";
import "leaflet/dist/leaflet.css";

const DALAT_CENTER = [11.9404, 108.4583];
const DALAT_BOUNDS = [
  [11.86, 108.39],
  [12.02, 108.55],
];

/** ~700m ô lưới — gom điểm cùng khu phố để tìm vùng tập trung nhất */
const DENSITY_GRID_DEG = 0.0065;

function isLeafletMapReady(map) {
  if (!map) return false;
  const container = map.getContainer?.() || map._container;
  return !!(map._loaded && map._mapPane && container && container.isConnected);
}

/**
 * Chỉ số các điểm thuộc ô lưới có nhiều điểm nhất (dùng tọa độ gốc, trước jitter).
 */
function indicesOfDensestCell(originalCoords) {
  const bins = new Map();
  originalCoords.forEach(([lat, lng], i) => {
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;
    const k = `${Math.floor(lat / DENSITY_GRID_DEG)},${Math.floor(lng / DENSITY_GRID_DEG)}`;
    if (!bins.has(k)) bins.set(k, []);
    bins.get(k).push(i);
  });
  let best = [];
  for (const arr of bins.values()) {
    if (arr.length > best.length) best = arr;
  }
  return best;
}

/**
 * Chọn tọa độ hiển thị để fit: nếu đa số nằm trong một ô — zoom vào cụm đó;
 * không thì fit toàn bộ (điểm rải đều).
 */
function displayPointsForSmartFit(originalCoords, displayCoords) {
  const n = originalCoords.length;
  if (n === 0) return [];
  if (n === 1) return displayCoords;

  const idx = indicesOfDensestCell(originalCoords);
  const inDensest = idx.length;
  const majority = inDensest > n / 2;
  const oneOutlier = inDensest >= Math.max(2, n - 1);
  const useCluster = inDensest >= 2 && (majority || oneOutlier);

  if (useCluster) return idx.map((i) => displayCoords[i]);
  return displayCoords;
}

function densestTargetFromPoints(originalCoords) {
  if (!Array.isArray(originalCoords) || originalCoords.length === 0) return null;
  if (originalCoords.length === 1) return originalCoords[0];
  const idx = indicesOfDensestCell(originalCoords);
  if (!idx || idx.length === 0) return null;
  let sumLat = 0;
  let sumLng = 0;
  let n = 0;
  idx.forEach((i) => {
    const p = originalCoords[i];
    if (!Array.isArray(p) || p.length < 2) return;
    const lat = p[0];
    const lng = p[1];
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    sumLat += lat;
    sumLng += lng;
    n += 1;
  });
  if (n === 0) return null;
  return [sumLat / n, sumLng / n];
}

function haversineKm(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length < 2 || b.length < 2) return null;
  const lat1 = a[0];
  const lon1 = a[1];
  const lat2 = b[0];
  const lon2 = b[1];
  if (![lat1, lon1, lat2, lon2].every(Number.isFinite)) return null;
  const R = 6371;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const s1 = Math.sin(dLat / 2);
  const s2 = Math.sin(dLon / 2);
  const x = s1 * s1 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * s2 * s2;
  const c = 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  return R * c;
}

// --- TỰ FIT MAP: nếu đã định vị được người dùng → khung nhìn gồm bạn + mọi điểm trên bản đồ ---
const FitMapView = ({ originalPoints, displayPoints, fullScreen, userPos, preferDensestOnLoad, densestTarget }) => {
  const map = useMap();
  const didPreferRef = useRef(false);
  const lockFitRef = useRef(false);

  useEffect(() => {
    if (!isLeafletMapReady(map)) return;
    const hasUser =
      Array.isArray(userPos) &&
      userPos.length === 2 &&
      Number.isFinite(userPos[0]) &&
      Number.isFinite(userPos[1]);

    // If we just preferred densest on load, avoid other auto-fit logic overriding it
    // while async state (geolocation / data) settles.
    if (lockFitRef.current) return;

    if (
      !hasUser &&
      fullScreen &&
      preferDensestOnLoad &&
      !didPreferRef.current &&
      Array.isArray(densestTarget) &&
      densestTarget.length === 2 &&
      Number.isFinite(densestTarget[0]) &&
      Number.isFinite(densestTarget[1])
    ) {
      didPreferRef.current = true;
      lockFitRef.current = true;
      try {
        if (!isLeafletMapReady(map)) return;
        map.setView(densestTarget, 15, { animate: false });
      } catch {
        // ignore
      }
      // release lock after first render settles
      window.setTimeout(() => {
        lockFitRef.current = false;
      }, 900);
      return;
    }

    if (hasUser) {
      if (displayPoints.length === 0) {
        try {
          if (!isLeafletMapReady(map)) return;
          map.setView(userPos, fullScreen ? 15 : 14);
        } catch {
          // ignore
        }
        return;
      }
      try {
        if (!isLeafletMapReady(map)) return;
        const bounds = L.latLngBounds([userPos]);
        displayPoints.forEach((p) => {
          if (Array.isArray(p) && p.length >= 2 && Number.isFinite(p[0]) && Number.isFinite(p[1])) {
            bounds.extend(p);
          }
        });
        map.fitBounds(bounds, {
          padding: fullScreen ? [44, 44] : [36, 36],
          maxZoom: fullScreen ? 16 : 15,
          animate: false,
        });
      } catch {
        // ignore
      }
      return;
    }

    const fitPts = displayPointsForSmartFit(originalPoints, displayPoints);

    if (fitPts.length === 0) {
      try {
        if (!isLeafletMapReady(map)) return;
        if (fullScreen) {
          map.fitBounds(DALAT_BOUNDS, { padding: [20, 20], maxZoom: 13, animate: false });
        } else {
          map.setView(DALAT_CENTER, 13, { animate: false });
        }
      } catch {
        // ignore
      }
      return;
    }

    if (fitPts.length === 1) {
      try {
        if (!isLeafletMapReady(map)) return;
        map.setView(fitPts[0], fullScreen ? 16 : 15, { animate: false });
      } catch {
        // ignore
      }
      return;
    }

    try {
      if (!isLeafletMapReady(map)) return;
      const bounds = L.latLngBounds(fitPts);
      map.fitBounds(bounds, {
        padding: fullScreen ? [28, 28] : [30, 30],
        maxZoom: fullScreen ? 17 : 16,
        animate: false,
      });
    } catch {
      // ignore
    }
  }, [map, originalPoints, displayPoints, fullScreen, userPos, preferDensestOnLoad, densestTarget]);

  return null;
};

const IntroRoute = ({
  enabled,
  from,
  to,
  durationMs = 2500,
  startDelayMs = 650,
  zoomToLevel = 16,
  onFinished,
}) => {
  const map = useMap();
  const [t, setT] = useState(0);
  const didZoomRef = useRef(false);
  const [finished, setFinished] = useState(false);

  const ok =
    enabled &&
    Array.isArray(from) &&
    from.length === 2 &&
    Number.isFinite(from[0]) &&
    Number.isFinite(from[1]) &&
    Array.isArray(to) &&
    to.length === 2 &&
    Number.isFinite(to[0]) &&
    Number.isFinite(to[1]);

  const fromLat = ok ? from[0] : 0;
  const fromLng = ok ? from[1] : 0;
  const toLat = ok ? to[0] : 0;
  const toLng = ok ? to[1] : 0;

  const angleDeg = useMemo(() => {
    if (!ok) return 0;
    if (!isLeafletMapReady(map)) return 0;
    try {
      const a = map.latLngToLayerPoint(L.latLng(fromLat, fromLng));
      const b = map.latLngToLayerPoint(L.latLng(toLat, toLng));
      return (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
    } catch {
      return 0;
    }
  }, [ok, map, fromLat, fromLng, toLat, toLng]);

  const arrowIcon = useMemo(
    () =>
      L.divIcon({
        className: "",
        html: `<div class="intro-arrow" style="transform: rotate(${angleDeg}deg)"></div>`,
        iconSize: [28, 28],
        iconAnchor: [14, 14],
      }),
    [angleDeg]
  );

  useEffect(() => {
    if (!ok) return;
    let raf = 0;
    const start = performance.now();
    const step = (now) => {
      const elapsed = now - start;
      const p = Math.min(1, Math.max(0, (elapsed - startDelayMs) / durationMs));
      setT(p);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    setT(0);
    didZoomRef.current = false;
    setFinished(false);
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [ok, durationMs, startDelayMs, fromLat, fromLng, toLat, toLng]);

  useEffect(() => {
    if (!ok) return;
    if (t < 1) return;
    if (didZoomRef.current) return;
    didZoomRef.current = true;
    let doneTimer = 0;
    try {
      if (!isLeafletMapReady(map)) return;
      map.stop?.();
      // Tránh lỗi `_leaflet_pos` khi unmount/route change lúc đang animate.
      map.setView(to, zoomToLevel, { animate: false });
      doneTimer = window.setTimeout(() => {
        setFinished(true);
        onFinished?.();
      }, 350);
    } catch {
      // ignore
    }
    return () => {
      if (doneTimer) window.clearTimeout(doneTimer);
    };
  }, [ok, t, map, to, zoomToLevel, onFinished]);

  useEffect(() => {
    if (!ok) return;
    try {
      // đảm bảo map ổn định khi overlay animation bắt đầu
      if (!isLeafletMapReady(map)) return;
      map.invalidateSize?.({ animate: false });
    } catch {
      // ignore
    }
  }, [ok, map]);

  if (!ok || finished) return null;

  const positions = [from, to];
  const lat = from[0] + (to[0] - from[0]) * t;
  const lng = from[1] + (to[1] - from[1]) * t;

  return (
    <>
      <style>{`
        @keyframes introDashMove {
          0% { stroke-dashoffset: 40; opacity: 0.15; }
          15% { opacity: 0.9; }
          100% { stroke-dashoffset: 0; opacity: 0.9; }
        }
        @keyframes introPulse {
          0% { transform: scale(0.9); opacity: 0.55; }
          70% { transform: scale(1.2); opacity: 0.12; }
          100% { transform: scale(1.2); opacity: 0; }
        }
        .intro-route {
          stroke: rgba(239, 68, 68, 0.95);
          stroke-width: 4;
          stroke-linecap: round;
          stroke-linejoin: round;
          stroke-dasharray: 12 10;
          animation: introDashMove 2600ms ease-out forwards;
          filter: drop-shadow(0 8px 14px rgba(0,0,0,0.18));
        }
        .intro-arrow{
          width: 28px;
          height: 28px;
          border-radius: 9999px;
          background: rgba(255,255,255,0.92);
          box-shadow: 0 14px 30px rgba(0,0,0,0.22);
          position: relative;
        }
        .intro-arrow:before{
          content:"";
          position:absolute;
          left: 7px;
          top: 9px;
          width: 0;
          height: 0;
          border-top: 5px solid transparent;
          border-bottom: 5px solid transparent;
          border-left: 11px solid rgba(239,68,68,0.95);
          filter: drop-shadow(0 2px 3px rgba(0,0,0,0.18));
        }
        .intro-target {
          filter: drop-shadow(0 10px 16px rgba(0,0,0,0.25));
        }
        .intro-target-pulse {
          transform-origin: center;
          animation: introPulse 1800ms ease-out infinite;
        }
      `}</style>

      {t > 0 && (
        <>
          <Polyline
            positions={positions}
            pathOptions={{
              className: "intro-route",
            }}
          />

          <Marker position={[lat, lng]} icon={arrowIcon} interactive={false} keyboard={false} />
        </>
      )}

      <CircleMarker
        center={to}
        radius={8}
        className="intro-target"
        pathOptions={{
          color: "#ffffff",
          weight: 3,
          fillColor: "#ef4444",
          fillOpacity: 1,
        }}
      />
      <CircleMarker
        center={to}
        radius={16}
        className="intro-target-pulse"
        pathOptions={{
          color: "#ef4444",
          weight: 2,
          fillColor: "#ef4444",
          fillOpacity: 0.15,
        }}
      />
    </>
  );
};

function stripHtmlForPopup(s) {
  if (s == null) return "";
  return String(s).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

/** Giới thiệu tối đa ~50 từ (theo wireframe) */
function truncateWords(text, maxWords = 50) {
  const t = stripHtmlForPopup(text);
  if (!t) return "";
  const w = t.split(/\s+/).filter(Boolean);
  if (w.length <= maxWords) return t;
  return `${w.slice(0, maxWords).join(" ")}…`;
}

/** Bay tới điểm: ưu tiên tọa độ sau jitter (trùng khớp marker), không thì lat/lng gửi kèm */
const FlyToTarget = ({ request, jitteredData, zoom }) => {
  const map = useMap();
  const jitteredRef = useRef(jitteredData);
  jitteredRef.current = jitteredData;
  useEffect(() => {
    if (!request || request.nonce == null) return;
    if (!isLeafletMapReady(map)) return;
    const list = jitteredRef.current || [];
    let coord;
    if (request.locationId != null) {
      const found = list.find((j) => String(j.id) === String(request.locationId));
      if (found?.coords) coord = found.coords;
    }
    if (!coord && Number.isFinite(request.lat) && Number.isFinite(request.lng)) {
      coord = [request.lat, request.lng];
    }
    if (!coord || !Number.isFinite(coord[0]) || !Number.isFinite(coord[1])) return;
    // Leaflet có thể ném lỗi `_leaflet_pos` nếu zoom animation kết thúc đúng lúc map bị unmount/resize.
    // Dùng setView không animate để ổn định hơn trong React lifecycle.
    try {
      if (!isLeafletMapReady(map)) return;
      map.stop?.();
      map.setView(coord, zoom ?? 16, { animate: false });
    } catch {
      // ignore
    }
  }, [map, zoom, request]);
  return null;
};

const InvalidateSizeOnTrigger = ({ trigger }) => {
  const map = useMap();
  useEffect(() => {
    // Leaflet không tự cập nhật size khi container đổi chiều ngang (sidebar open/close).
    const t1 = window.setTimeout(() => {
      try {
        if (!isLeafletMapReady(map)) return;
        map.invalidateSize?.({ animate: false });
      } catch {
        // ignore
      }
    }, 60);
    const t2 = window.setTimeout(() => {
      try {
        if (!isLeafletMapReady(map)) return;
        map.invalidateSize?.({ animate: false });
      } catch {
        // ignore
      }
    }, 240);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [map, trigger]);
  return null;
};

const InvalidateSizeOnWindowResize = () => {
  const map = useMap();
  useEffect(() => {
    const onResize = () => {
      try {
        if (!isLeafletMapReady(map)) return;
        map.invalidateSize?.({ animate: false });
      } catch {
        // ignore
      }
    };
    // Safari iOS / Android: orientation change sometimes doesn't fire a stable resize immediately
    const onOrientation = () => {
      window.setTimeout(onResize, 50);
      window.setTimeout(onResize, 250);
    };
    window.addEventListener("resize", onResize, { passive: true });
    window.addEventListener("orientationchange", onOrientation, { passive: true });
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onOrientation);
    };
  }, [map]);
  return null;
};

// --- POPUP (wireframe: icon + tên, địa chỉ/SĐT/giới thiệu ≤50 từ, 3 cột video+ảnh, chỉ đường / thông tin thêm) ---
const popupEmptyMedia = (
  <div className="flex h-full w-full items-center justify-center bg-[#efe6d4]">
    <span className="text-[10px] font-semibold text-[#8a7f66]">Đang cập nhật</span>
  </div>
);

const MapPopupMediaImg = ({ src }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
  }, [src]);
  if (!src || failed) return popupEmptyMedia;
  return (
    <img
      src={src}
      alt=""
      className="h-full w-full object-cover"
      onError={() => setFailed(true)}
    />
  );
};

function getUrlFromAsset(input) {
  if (!input) return "";

  if (typeof input === "string") {
    return input.trim();
  }

  return String(
    input.url ||
      input.src ||
      input.imageUrl ||
      input.image?.url ||
      ""
  ).trim();
}

function uniqueUsableUrls(list = []) {
  const seen = new Set();

  return list
    .map(getUrlFromAsset)
    .filter((url) => {
      if (!hasUsableImageUrl(url)) return false;

      const key = url
        .split("?")[0]
        .replace(/^https?:\/\//i, "")
        .replace(/^cdn\.longvongdalat\.com\//i, "")
        .replace(/%20/g, " ")
        .toLowerCase()
        .trim();

      if (seen.has(key)) return false;

      seen.add(key);
      return true;
    });
}

function getPopupImages(item) {
  const heroGallery = Array.isArray(item?.heroGallery) ? item.heroGallery : [];
  const gallery = Array.isArray(item?.gallery) ? item.gallery : [];
  const mediaBlocks = Array.isArray(item?.mediaBlocks) ? item.mediaBlocks : [];

  return uniqueUsableUrls([
    ...heroGallery,
    ...gallery,
    item?.image,
    ...mediaBlocks
      .filter((b) => String(b?.type || "").toLowerCase() === "image")
      .map((b) => b?.image),
  ]).slice(0, 3);
}

const MapPopupCard = ({ item, isFav, onToggleFav }) => {
  const navigate = useNavigate();
  const addressLine = hasFormattedAddress(item) ? formatLocationAddress(item) : "";

  const lat = item.lat ?? item.coords?.[0] ?? 11.9404;
  const lng = item.lng ?? item.coords?.[1] ?? 108.4583;

  const handleDirection = () => {
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, "_blank");
  };

  const handleMoreInfo = () => {
    const slug = item.slug ? String(item.slug).trim() : "";
    if (slug) {
      try {
        locationService.bumpViewBySlug(slug).catch(() => {});
      } catch {
        // ignore
      }
      navigate(`/location/${encodeURIComponent(slug)}`);
      return;
    }
    navigate("/locations");
  };

  const homeLinkUrl = String(item?.homeLinkUrl || "").trim();
  const homeLinkLabel = String(item?.homeLinkLabel || "").trim();
  const handleHomeLink = async () => {
    if (!homeLinkUrl) return;
    try {
      const base = (process.env.REACT_APP_API_URL || "http://localhost:8081").replace(/\/$/, "");
      const id = encodeURIComponent(String(item.id || item._id || ""));
      if (id) {
        fetch(`${base}/api/locations/${id}/home-link/click`, { method: "POST" }).catch(() => {});
      }
    } catch {
      // ignore
    }
    window.open(homeLinkUrl, "_blank", "noopener,noreferrer");
  };

  const [detail, setDetail] = useState(null);

  useEffect(() => {
    const slug = String(item?.slug || "").trim();

    if (!slug) {
      setDetail(null);
      return;
    }

    let alive = true;

    locationService
      .getLocationBySlug(slug, { incView: false })
      .then((data) => {
        if (!alive) return;
        setDetail(data || null);
      })
      .catch(() => {
        if (!alive) return;
        setDetail(null);
      });

    return () => {
      alive = false;
    };
  }, [item?.slug]);

  const popupSource = detail
    ? {
        ...item,
        ...detail,

        // giữ lại dữ liệu map đã jitter, tránh mất tọa độ marker
        id: item.id,
        lat: item.lat,
        lng: item.lng,
        coords: item.coords,
        category: item.category,
        slug: item.slug || detail.slug,
      }
    : item;

  const popupImages = getPopupImages(popupSource);
  const imgCol1 = popupImages[0] || "";
  const imgCol2 = popupImages[1] || "";
  const imgCol3 = popupImages[2] || "";

  const introRaw = popupSource.shortIntro || popupSource.seoDescription || "";
  const intro = truncateWords(introRaw, 50);

  const categoryLabel =
  popupSource.category === "food" ? "Ăn uống"
  : popupSource.category === "stay" ? "Homestay"
  : "Văn hoá";

  const infoRow = (label, content) => (
    <div className="flex gap-2">
      <span className="w-14 shrink-0 text-[10px] font-extrabold uppercase tracking-wider text-[#8a7f66]">{label}</span>
      <span className="min-w-0 flex-1 text-[#4a4638]">{content}</span>
    </div>
  );

  const ghostBtn =
    "flex items-center justify-center gap-1.5 rounded-xl border border-[#cfc3a8] bg-[#fffaf0] py-2.5 text-xs font-bold text-[#4a4638] transition hover:border-[#5f745f] hover:bg-white hover:text-[#3f5340]";

  return (
    <div style={{ width: "min(420px, calc(100vw - 48px))" }} className="font-sans text-[#2f352c]">
      <div className="overflow-hidden rounded-2xl border border-[#cfc3a8] bg-[#f7f1e6] shadow-[0_18px_44px_rgba(54,48,35,0.28)] ring-4 ring-[#fffaf0]/70">
        {/* Header: danh mục + tên */}
        <div className="flex items-start gap-3 border-b border-[#d9ccb4] bg-[linear-gradient(180deg,#697961_0%,#556652_100%)] px-4 py-3 text-white">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/30 bg-white/15">
            <MapPin size={19} strokeWidth={2} aria-hidden />
          </div>
          <div className="min-w-0 flex-1 pr-6">
            <p className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-white/70">{categoryLabel}</p>
            <h3 className="line-clamp-2 text-left font-faustina text-lg font-bold italic leading-snug text-[#fff9ef]">
              {popupSource.name || popupSource.title}
            </h3>
          </div>
          {typeof onToggleFav === "function" && typeof isFav === "boolean" && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleFav?.(item.id);
              }}
              className={`mr-5 shrink-0 rounded-full border p-2 transition ${
                isFav
                  ? "border-[#e8b9b3] bg-[#fff1ee] text-[#a85d5d]"
                  : "border-white/30 bg-white/10 text-white hover:bg-white/20"
              }`}
              title={isFav ? "Bỏ yêu thích" : "Yêu thích"}
              aria-pressed={isFav}
            >
              <Heart size={15} fill={isFav ? "currentColor" : "none"} />
            </button>
          )}
        </div>

        <div className="space-y-2 px-4 py-3 text-xs leading-relaxed">
          {infoRow("Địa chỉ", addressLine || "Đang cập nhật")}
          {infoRow(
            "SĐT",
            popupSource.phone ? (
              <a
                href={`tel:${String(popupSource.phone).replace(/\s/g, "")}`}
                className="font-semibold text-[#3f5340] underline-offset-2 hover:underline"
              >
                {popupSource.phone}
              </a>
            ) : (
              "Đang cập nhật"
            )
          )}
          <div className="border-t border-dashed border-[#d9ccb4] pt-2">
            <p className="mb-0.5 text-[10px] font-extrabold uppercase tracking-wider text-[#8a7f66]">Giới thiệu</p>
            <p className="text-[#4a4638]">{intro || "Đang cập nhật"}</p>
          </div>
        </div>

        {/* 3 ảnh (khung ảnh cũ) */}
        <div className="grid grid-cols-3 gap-2 px-4 pb-3">
          {[imgCol1, imgCol2, imgCol3].map((src, i) => (
            <div key={i} className="aspect-square overflow-hidden rounded-lg border-2 border-[#fffaf0] bg-[#efe6d4] shadow-[0_3px_8px_rgba(80,65,40,0.18)]">
              <MapPopupMediaImg src={src} />
            </div>
          ))}
        </div>

        {item._jittered && (
          <div className="mx-4 mb-2 rounded-lg border border-[#e6d3a3] bg-[#fbf1d6] px-2 py-1.5 text-[10px] font-medium text-[#7a5d1c]">
            Vị trí marker có thể tách nhẹ khi trùng tọa độ — kiểm tra trên trang chi tiết.
          </div>
        )}

        <div className={`grid gap-2 border-t border-[#d9ccb4] bg-[#f1e8d6] px-4 py-3 ${homeLinkUrl ? "grid-cols-3" : "grid-cols-2"}`}>
          <button type="button" onClick={handleDirection} className={ghostBtn}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg>
            Chỉ đường
          </button>
          {homeLinkUrl ? (
            <button type="button" onClick={handleHomeLink} className={ghostBtn} title={homeLinkUrl}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3h7v7"/><path d="M10 14L21 3"/><path d="M21 14v7H3V3h7"/></svg>
              {homeLinkLabel || "Mở link"}
            </button>
          ) : null}
          <button
            type="button"
            onClick={handleMoreInfo}
            className="flex items-center justify-center gap-1 rounded-xl bg-[#5f745f] py-2.5 text-xs font-bold text-white shadow-sm transition hover:bg-[#4f6250]"
          >
            Thông tin thêm
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </button>
        </div>
      </div>
    </div>
  );
};

/** Mở popup marker sau khi bay (đồng bộ với animation flyTo) */
const LocationMapMarker = ({ item, icon, popupRequest, isFav, onToggleFav }) => {
  const markerRef = useRef(null);
  useEffect(() => {
    if (!popupRequest?.openPopup || popupRequest.nonce == null) return;
    if (String(popupRequest.locationId) !== String(item.id)) return;
    const t = setTimeout(() => {
      const m = markerRef.current;
      if (m && typeof m.openPopup === "function") m.openPopup();
    }, 820);
    return () => clearTimeout(t);
  }, [popupRequest, item.id]);
  return (
    <Marker ref={markerRef} position={item.coords} icon={icon}>
      <Popup maxWidth={460} className="dalat-map-popup">
        <MapPopupCard item={item} isFav={isFav} onToggleFav={onToggleFav} />
      </Popup>
    </Marker>
  );
};

const MarkersInView = React.memo(function MarkersInView({
  data,
  popupRequest,
  isFavorite,
  toggleFavorite,
  tileStyle,
}) {
  const map = useMap();
  const [boundsKey, setBoundsKey] = useState(0);

  useEffect(() => {
    // kick initial bounds calculation after mount
    const t = window.setTimeout(() => setBoundsKey((k) => k + 1), 0);
    return () => window.clearTimeout(t);
  }, []);

  useMapEvents({
    moveend: () => setBoundsKey((k) => k + 1),
    zoomend: () => setBoundsKey((k) => k + 1),
  });

  const visible = useMemo(() => {
    const list = Array.isArray(data) ? data : [];
    if (!map || typeof map.getBounds !== "function") return list;
    try {
      // pad a bit so markers don't pop in/out too aggressively while dragging
      const b = map.getBounds().pad(0.12);
      return list.filter((item) => {
        const c = item?.coords;
        if (!Array.isArray(c) || c.length < 2) return false;
        return b.contains(c);
      });
    } catch {
      return list;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, map, boundsKey, tileStyle]);

  return (
    <>
      {visible.map((item) => (
        <LocationMapMarker
          key={item.id}
          item={item}
          icon={getCachedLocationIcon(
            { mapIcon: item.mapIcon, customUrl: item.mapIconAsset?.url || "" },
            item.category,
            item.categorySlug,
            tileStyle
          )}
          popupRequest={popupRequest}
          isFav={typeof isFavorite === "function" ? isFavorite(item.id) : false}
          onToggleFav={toggleFavorite}
        />
      ))}
    </>
  );
});

const Gmap = ({
  filteredData = [],
  fullScreen = false,
  showOpenButton = false,
  onOpenFullMap,
  tileStyle = "color",
  toggleFavorite,
  isFavorite,
  /** Trigger invalidating size when layout changes */
  invalidateSizeKey,
  /** Intro trên trang chủ: "title" -> "route" -> "none" */
  introPhase = "none",
  /** Trang bản đồ: ưu tiên zoom vào cụm đông nhất khi mở */
  preferDensestOnLoad = false,
  /**
   * { locationId, nonce, openPopup?: boolean } — bay tới marker (tọa độ sau jitter)
   * hoặc { lat, lng, nonce } (không mở popup)
   */
  flyToRequest = null,
}) => {
  // userPos = null means not yet determined; [lat, lng] = got it; false = denied
  const [userPos, setUserPos] = useState(null);
  const [locating, setLocating] = useState(false);
  const [userFlyReq, setUserFlyReq] = useState(null);
  const [quickFlyReq, setQuickFlyReq] = useState(null);
  const [currentTileStyle, setCurrentTileStyle] = useState(tileStyle);

  useEffect(() => {
    if (!navigator.geolocation) { setUserPos(false); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setUserPos([pos.coords.latitude, pos.coords.longitude]),
      ()    => setUserPos(false),
      { timeout: 8000 }
    );
  }, []);

  const requestLocateMe = () => {
    if (!navigator.geolocation) {
      setUserPos(false);
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const p = [pos.coords.latitude, pos.coords.longitude];
        setUserPos(p);
        setUserFlyReq({ lat: p[0], lng: p[1], nonce: Date.now() });
        setLocating(false);
      },
      () => {
        setUserPos(false);
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 30_000 }
    );
  };

  const normalizedData = useMemo(() => {
    return (filteredData || []).map((item, index) => {
      const lat =
        item.latitude ??
        item.lat ??
        item.coords?.[0] ??
        11.9404;

      const lng =
        item.longitude ??
        item.lng ??
        item.coords?.[1] ??
        108.4583;

      const { image: mapImage, gallery: mapGallery } = normalizeLocationMapMedia(item);

      return {
        ...item,
        id: item._id || item.id || index + 1,
        name: item.name || item.title || "Chưa có tên",
        title: item.title || item.name || "Chưa có tên",
        phone: item.phone || "",
        shortIntro: item.shortIntro || "",
        image: mapImage,
        gallery: mapGallery,
        heroGallery: Array.isArray(item.heroGallery) ? item.heroGallery : [],
        mediaBlocks: Array.isArray(item.mediaBlocks) ? item.mediaBlocks : [],
        videos: Array.isArray(item.videos) ? item.videos : [],
        tiktokEmbed: item.tiktokEmbed || "",
        tiktokUrl: item.tiktokUrl || "",
        slug: item.slug || `location-${index + 1}`,
        categorySlug: item.categorySlug || "du-lich-van-hoa",
        category:
          item.category ||
          (isFoodCategoryDisplayName(item.categoryName) ? "food" :
          item.categoryName === "Homestay" ? "stay" : "culture"),
        mapIcon: item.mapIcon != null && item.mapIcon !== "" ? String(item.mapIcon) : "all",
        lat: Number(lat),
        lng: Number(lng),
        coords: [Number(lat), Number(lng)],
      };
    });
  }, [filteredData]);

  // Spread pins that share the same coordinates in a spiral so they're all visible
  const jitteredData = useMemo(() => {
    const counter = {};
    return normalizedData.map((item) => {
      const key = `${item.lat.toFixed(4)},${item.lng.toFixed(4)}`;
      counter[key] = (counter[key] || 0) + 1;
      const n = counter[key];
      if (n === 1) return item;
      const angle = (n - 2) * (Math.PI * 2 / 8);
      const radius = 0.0012 * Math.ceil((n - 1) / 8);
      return {
        ...item,
        lat: item.lat + Math.cos(angle) * radius,
        lng: item.lng + Math.sin(angle) * radius,
        coords: [item.lat + Math.cos(angle) * radius, item.lng + Math.sin(angle) * radius],
        _jittered: true,
      };
    });
  }, [normalizedData]);

  /** Cùng thứ tự index: tọa độ gốc (gom cụm) + tọa độ hiển thị (sau jitter) */
  const { mapPoints, originalMapPoints } = useMemo(() => {
    const o = [];
    const d = [];
    jitteredData.forEach((j, i) => {
      const base = normalizedData[i];
      if (!base || Number.isNaN(base.lat) || Number.isNaN(base.lng)) return;
      if (Number.isNaN(j.lat) || Number.isNaN(j.lng)) return;
      o.push([base.lat, base.lng]);
      d.push(j.coords);
    });
    return { originalMapPoints: o, mapPoints: d };
  }, [normalizedData, jitteredData]);

  const densestTarget = useMemo(() => densestTargetFromPoints(originalMapPoints), [originalMapPoints]);
  const introDistanceKm = useMemo(() => {
    if (introPhase !== "route") return null;
    if (!Array.isArray(userPos) || !Array.isArray(densestTarget)) return null;
    const km = haversineKm(userPos, densestTarget);
    if (!Number.isFinite(km)) return null;
    return km;
  }, [introPhase, userPos, densestTarget]);

  const [introStage, setIntroStage] = useState("idle"); // idle | you | arrow | distance

  const goDalat = () => {
    const target =
      Array.isArray(densestTarget) && densestTarget.length === 2 && densestTarget.every(Number.isFinite)
        ? densestTarget
        : DALAT_CENTER;
    setQuickFlyReq({ lat: target[0], lng: target[1], nonce: Date.now() });
  };

  useEffect(() => {
    if (introPhase !== "route") {
      setIntroStage("idle");
      return;
    }
    if (!Array.isArray(userPos) || !Array.isArray(densestTarget)) {
      setIntroStage("idle");
      return;
    }
    setIntroStage("you");
    const tArrow = window.setTimeout(() => setIntroStage("arrow"), 650);
    return () => clearTimeout(tArrow);
  }, [introPhase, userPos, densestTarget]);

  return (
    <div
      className={`relative w-full overflow-hidden border border-[#d9d3c5] bg-[#fffdf8] shadow-inner ${
        fullScreen
          ? "h-full rounded-none"
          : "h-[450px] rounded-[2.5rem]"
      }`}
    >
      {introStage !== "idle" && (
        <div className="pointer-events-none absolute left-1/2 top-4 z-[1200] -translate-x-1/2 px-3">
          <div className="rounded-3xl border border-white/20 bg-gradient-to-b from-white/14 via-white/10 to-white/6 px-4 py-3 text-white shadow-[0_18px_60px_rgba(0,0,0,0.35)] backdrop-blur-xl">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/15">
                  <MapPin size={18} className="text-white/90" />
                </div>
                <span className="absolute -bottom-1 -right-1 h-3.5 w-3.5 rounded-full bg-emerald-400 ring-2 ring-black/30" />
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-3">
                  <p className="truncate text-[12px] font-extrabold tracking-wide">
                    {introStage === "you" ? "Vị trí của bạn" : "Đà Lạt"}
                  </p>
                  <span className="shrink-0 rounded-full bg-black/25 px-2.5 py-1 text-[10px] font-bold tracking-widest text-white/85 ring-1 ring-white/10">
                    INTRO
                  </span>
                </div>

                <p className="mt-0.5 text-[11px] font-medium text-white/78">
                  {introStage === "you"
                    ? "Đang xác định điểm xuất phát…"
                    : introStage === "arrow"
                      ? "Đang bay tới Đà Lạt…"
                      : "Hoàn tất — sẵn sàng khám phá"}
                </p>

                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-black/22 px-2.5 py-1 text-[10px] font-semibold text-white/90 ring-1 ring-white/10">
                    {introDistanceKm != null ? `${introDistanceKm.toFixed(1)} km` : "… km"}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-black/25 ring-1 ring-white/10">
              <div
                className={`h-full rounded-full bg-gradient-to-r from-emerald-400 via-emerald-300 to-white/80 transition-all duration-700 ${
                  introStage === "you" ? "w-[22%]" : introStage === "arrow" ? "w-[68%]" : "w-full"
                }`}
              />
            </div>
          </div>
        </div>
      )}
      {/* Bottom-right controls: locate + style */}
      <div className="absolute bottom-6 right-4 z-[1000] flex items-center gap-2">
        <div className="flex h-11 items-center rounded-full border border-[#cfc3a8] bg-[#fffaf0]/95 p-1 shadow-[0_10px_24px_rgba(54,48,35,0.16)] backdrop-blur-sm">
          <button
            type="button"
            onClick={requestLocateMe}
            title="Vị trí của bạn"
            aria-label="Vị trí của bạn"
            className="inline-flex h-full items-center gap-1.5 rounded-full px-3 text-[12px] font-bold text-[#4a4638] transition hover:bg-[#efe6d4] hover:text-[#3f5340] disabled:cursor-not-allowed disabled:opacity-60"
            disabled={locating}
          >
            {locating ? <Loader2 size={15} className="animate-spin" /> : <LocateFixed size={15} />}
            <span className="hidden sm:inline">Vị trí của bạn</span>
          </button>
          <span className="mx-0.5 h-5 w-px bg-[#d9ccb4]" aria-hidden />
          <button
            type="button"
            onClick={goDalat}
            title="Về Đà Lạt"
            aria-label="Về Đà Lạt"
            className="inline-flex h-full items-center gap-1.5 rounded-full px-3 text-[12px] font-bold text-[#4a4638] transition hover:bg-[#efe6d4] hover:text-[#3f5340]"
          >
            <MapPin size={15} />
            <span className="hidden sm:inline">Đà Lạt</span>
          </button>
        </div>

        <div
          className="flex h-11 items-center gap-0.5 rounded-full border border-[#cfc3a8] bg-[#fffaf0]/95 p-1 shadow-[0_10px_24px_rgba(54,48,35,0.16)] backdrop-blur-sm"
          role="radiogroup"
          aria-label="Kiểu bản đồ"
        >
          {[
            { id: "color", title: "Bản đồ màu", icon: MapIcon },
            { id: "satellite", title: "Vệ tinh", icon: Satellite },
          ].map(({ id, title, icon: Icon }) => (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={currentTileStyle === id}
              onClick={() => setCurrentTileStyle(id)}
              title={title}
              className={`inline-flex h-full items-center gap-1.5 rounded-full px-3 text-[12px] font-bold transition-all ${
                currentTileStyle === id
                  ? "bg-[#5f745f] text-white shadow-[0_4px_10px_rgba(95,116,95,0.35)]"
                  : "text-[#4a4638] hover:bg-[#efe6d4] hover:text-[#3f5340]"
              }`}
            >
              <Icon size={14} />
              <span className="hidden sm:inline">{title}</span>
            </button>
          ))}
        </div>
      </div>

      {showOpenButton && !fullScreen && (
        <button
          onClick={onOpenFullMap}
          className="absolute top-4 right-4 z-[1000] inline-flex items-center gap-2 rounded-full border border-[#d9d3c5] bg-[#fffdf8]/95 px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] text-primary shadow-md"
        >
          <Expand size={14} />
          Xem toàn bản đồ
        </button>
      )}

      {userPos === false && (
        <div className="absolute top-4 left-1/2 z-[1000] -translate-x-1/2 whitespace-nowrap rounded-full border border-amber-100 bg-[#fffdf8]/90 px-4 py-2 shadow-sm backdrop-blur-md">
          <p className="text-[10px] text-amber-700 font-semibold uppercase tracking-widest">
            Không lấy được vị trí của bạn
          </p>
        </div>
      )}
      {userPos === null && (
        <div className="absolute top-4 left-1/2 z-[1000] flex -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-full border border-[#d9d3c5] bg-[#fffdf8]/90 px-4 py-2 shadow-sm backdrop-blur-md">
          <Loader2 size={12} className="animate-spin text-gray-400" />
          <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-widest">Đang định vị...</p>
        </div>
      )}

      <MapContainer
        key={`${fullScreen ? "fs" : "embed"}:${currentTileStyle}`}
        center={fullScreen ? DALAT_CENTER : (Array.isArray(userPos) ? userPos : DALAT_CENTER)}
        zoom={13}
        preferCanvas
        tap={false}
        scrollWheelZoom
        attributionControl={false}
        zoomControl={false}
        style={{ height: "100%", width: "100%" }}
      >
        <style>{`
          /* Đưa zoom +/- xuống góc phải dưới, nằm trên cụm nút style map */
          .leaflet-bottom.leaflet-right {
            right: 16px;
            bottom: 16px;
          }
          .leaflet-control-zoom {
            margin-bottom: 72px !important; /* chừa chỗ cho nút "Bản đồ màu / Vệ tinh" */
            border: 1px solid #cfc3a8 !important;
            border-radius: 9999px !important;
            overflow: hidden;
            padding: 4px;
            background: rgba(255, 250, 240, 0.95);
            box-shadow: 0 10px 24px rgba(54, 48, 35, 0.16) !important;
          }
          .leaflet-control-zoom a {
            width: 34px !important;
            height: 34px !important;
            line-height: 32px !important;
            border: 0 !important;
            border-radius: 9999px !important;
            background: transparent !important;
            color: #4a4638 !important;
            font-size: 18px !important;
            font-weight: 700;
          }
          .leaflet-control-zoom a + a {
            margin-top: 2px;
          }
          .leaflet-control-zoom a:hover {
            background: #efe6d4 !important;
            color: #3f5340 !important;
          }
          .leaflet-control-zoom a.leaflet-disabled {
            opacity: 0.4;
            background: transparent !important;
          }
          /* Mobile: prevent browser gestures interfering with Leaflet drag */
          .leaflet-container {
            touch-action: pan-x pan-y;
          }
        `}</style>
        <ZoomControl position="bottomright" />
        <AttributionControl position="bottomleft" prefix={false} />
        <InvalidateSizeOnTrigger trigger={invalidateSizeKey} />
        <InvalidateSizeOnWindowResize />
        {/* Intro effect: thu nhỏ "vị trí của tôi" trước khi vẽ mũi tên */}
        <style>{`
          @keyframes introYouShrink {
            0%   { transform: scale(1); opacity: 1; }
            70%  { transform: scale(0.72); opacity: 0.95; }
            100% { transform: scale(0.62); opacity: 0.92; }
          }
          .intro-you-shrink {
            transform-origin: center;
            transform-box: fill-box;
            animation: introYouShrink 650ms ease-out forwards;
          }
        `}</style>

        {currentTileStyle === "satellite" ? (
          <TileLayer
            attribution="Tiles © Esri — Source: Esri, Maxar, Earthstar Geographics"
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          />
        ) : (
          <OpenFreeMapLayer />
        )}

        <FitMapView
          originalPoints={originalMapPoints}
          displayPoints={mapPoints}
          fullScreen={fullScreen}
          userPos={Array.isArray(userPos) ? userPos : null}
          preferDensestOnLoad={preferDensestOnLoad}
          densestTarget={densestTarget}
        />

        <IntroRoute
          enabled={introPhase === "route" && introStage !== "idle"}
          from={Array.isArray(userPos) ? userPos : null}
          to={densestTarget}
          zoomToLevel={fullScreen ? 16 : 15}
          durationMs={2500}
          startDelayMs={650}
          onFinished={() => setIntroStage("distance")}
        />

        <FlyToTarget
          request={flyToRequest}
          jitteredData={jitteredData}
          zoom={fullScreen ? 17 : 16}
        />

        <FlyToTarget request={userFlyReq} jitteredData={jitteredData} zoom={fullScreen ? 17 : 16} />
        <FlyToTarget request={quickFlyReq} jitteredData={jitteredData} zoom={fullScreen ? 16 : 15} />

        <MarkersInView
          data={jitteredData}
          popupRequest={flyToRequest}
          isFavorite={isFavorite}
          toggleFavorite={toggleFavorite}
          tileStyle={currentTileStyle}
        />

        {Array.isArray(userPos) && (
          <CircleMarker
            center={userPos}
            radius={9}
            className={introStage === "you" ? "intro-you-shrink" : undefined}
            pathOptions={{
              color: "#ffffff",
              weight: currentTileStyle === "satellite" ? 4 : 3,
              fillColor: "#ef4444",
              fillOpacity: 1,
            }}
          >
            <Popup>
              <div className="text-sm font-medium">Vị trí của bạn</div>
            </Popup>
          </CircleMarker>
        )}
      </MapContainer>
    </div>
  );
};

export default Gmap;